import { Fragment, useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  paymentReconciliationApi,
  type ReconciliationActivityRow,
} from "@/lib/api/payment-reconciliation";
import { badgeClass, formatCurrency } from "./reconciliation-shared";
import { UpdateOrderStatusDialog } from "./UpdateOrderStatusDialog";

export function AlertsTab() {
  const [rows, setRows] = useState<ReconciliationActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openNoteId, setOpenNoteId] = useState<string | null>(null);
  const [statusRow, setStatusRow] = useState<ReconciliationActivityRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentReconciliationApi.getAlerts();
      setRows(res.data.rows);
    } catch {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="pr-card">
        <div className="pr-card-head">
          <div>
            <h2>Alerts</h2>
            <p>Mismatch alerts in table format. Notes open below the selected table row for a cleaner review.</p>
          </div>
        </div>
        <div className="pr-table-wrap">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Email / Phone</th>
                  <th>Amount</th>
                  <th>Payment Date</th>
                  <th>Mismatch Tag</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-muted-foreground">
                      No alerts found
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <Fragment key={row.id}>
                      <tr>
                        <td className="pr-student-cell">
                          <strong>{row.student}</strong>
                          <span>{row.program}</span>
                        </td>
                        <td>
                          {row.email}
                          <br />
                          <span style={{ color: "#64748b" }}>{row.phone}</span>
                        </td>
                        <td>
                          <strong>{formatCurrency(row.amount)}</strong>
                        </td>
                        <td>{row.date}</td>
                        <td>
                          <span className={`pr-badge ${badgeClass(row.result, row.result)}`}>{row.result}</span>
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="pr-pill-btn"
                              onClick={() => setOpenNoteId(openNoteId === row.id ? null : row.id)}
                            >
                              View Notes
                            </button>
                            {row.orderId && (
                              <button
                                type="button"
                                className="pr-pill-btn"
                                onClick={() => setStatusRow(row)}
                              >
                                Update Status
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      <tr className={openNoteId === row.id ? "" : "hidden"}>
                        <td colSpan={6} style={{ background: "var(--muted)", padding: 16 }}>
                          <div className="pr-recommendation">
                            <strong>Mismatch note:</strong> {row.reason}
                            <br />
                            <br />
                            <strong>Recommended action:</strong> {row.action}
                          </div>
                        </td>
                      </tr>
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <UpdateOrderStatusDialog
        open={Boolean(statusRow)}
        orderId={statusRow?.orderId ?? null}
        onClose={() => setStatusRow(null)}
        onSuccess={() => {
          setStatusRow(null);
          void load();
        }}
      />
    </>
  );
}
